/**
 * PRDList Component
 *
 * Display a list of PRDs for a repository with actions
 */

import * as React from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import {
  FileText,
  Eye,
  Edit,
  Trash2,
  AlertCircle,
  CheckCircle2,
  Clock,
  Archive,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { usePRDList, useDeletePRD } from '@/hooks/usePRD';
import { useToast } from '@/shared/hooks/useToast';
import type { PRD, PRDStatus } from '@forgenetwork/types';

interface PRDListProps {
  repositoryId: string;
}

/**
 * Status badge configuration
 */
const statusConfig: Record<
  PRDStatus,
  {
    variant: 'default' | 'secondary' | 'destructive';
    icon: React.ComponentType<{ className?: string }>;
    label: string;
  }
> = {
  draft: { variant: 'secondary', icon: Clock, label: 'Draft' },
  published: { variant: 'default', icon: CheckCircle2, label: 'Published' },
  archived: { variant: 'destructive', icon: Archive, label: 'Archived' },
};

/**
 * Format date for display
 */
function formatDate(date: Date | string): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/**
 * PRDList - Display list of PRDs for a repository
 */
export function PRDList({ repositoryId }: PRDListProps): React.JSX.Element {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data, isLoading, error } = usePRDList(repositoryId);
  const deletePRD = useDeletePRD();

  const handleView = (prdId: string): void => {
    navigate({ to: '/prd/$prdId', params: { prdId } });
  };

  const handleEdit = (prdId: string): void => {
    navigate({ to: '/prd/$prdId', params: { prdId }, search: { tab: 'edit' } });
  };

  const handleDelete = (prd: PRD): void => {
    deletePRD.mutate(
      { prdId: prd.id, repoId: repositoryId },
      {
        onSuccess: () => {
          toast({
            title: 'PRD Deleted',
            description: `"${prd.title}" has been deleted.`,
          });
        },
        onError: (error: Error) => {
          toast({
            title: 'Delete Failed',
            description: error.message || 'Failed to delete PRD',
            variant: 'destructive',
          });
        },
      }
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <div key={i} className="h-32 bg-muted animate-pulse rounded-xl" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <Card className="border-destructive">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-destructive">
            <AlertCircle className="h-5 w-5" />
            <p>{error instanceof Error ? error.message : 'Failed to load PRDs'}</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data || data.prds.length === 0) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FileText className="h-12 w-12 text-muted-foreground opacity-50 mb-4" />
            <h3 className="text-lg font-semibold mb-2">No PRDs Found</h3>
            <p className="text-sm text-muted-foreground max-w-md">
              No Product Requirements Documents have been generated for this repository
              yet. Generate your first PRD from a completed analysis.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total PRDs
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{data.total}</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Drafts
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {data.prds.filter((p) => p.status === 'draft').length}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Published
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {data.prds.filter((p) => p.status === 'published').length}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Archived
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">
              {data.prds.filter((p) => p.status === 'archived').length}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* PRD Cards */}
      <div className="space-y-4">
        {data.prds
          .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
          .map((prd) => {
            const StatusIcon = statusConfig[prd.status].icon;

            return (
              <Card
                key={prd.id}
                className={cn(
                  'transition-all hover:shadow-md',
                  prd.status === 'archived' && 'opacity-60'
                )}
              >
                <CardHeader>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 space-y-2">
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-primary" />
                        <CardTitle className="text-xl">{prd.title}</CardTitle>
                      </div>
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Badge
                          variant={statusConfig[prd.status].variant}
                          className="gap-1"
                        >
                          <StatusIcon className="h-3 w-3" />
                          {statusConfig[prd.status].label}
                        </Badge>
                        <span>Version {prd.version}</span>
                        <span>•</span>
                        <span>{formatDate(prd.createdAt)}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleView(prd.id)}
                        className="gap-2"
                      >
                        <Eye className="h-4 w-4" />
                        View
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(prd.id)}
                        className="gap-2"
                      >
                        <Edit className="h-4 w-4" />
                        Edit
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="outline"
                            className="gap-2 text-destructive hover:bg-destructive hover:text-destructive-foreground"
                          >
                            <Trash2 className="h-4 w-4" />
                            Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete PRD?</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{prd.title}"? This action
                              cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDelete(prd)}
                              className="bg-destructive hover:bg-destructive/90"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardHeader>

                <CardContent>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div>
                      <p className="text-muted-foreground mb-1">Sections</p>
                      <p className="font-medium">{prd.sections?.length ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Features</p>
                      <p className="font-medium">{prd.features?.length ?? 0}</p>
                    </div>
                    <div>
                      <p className="text-muted-foreground mb-1">Last Updated</p>
                      <p className="font-medium">
                        {prd.updatedAt ? formatDate(prd.updatedAt) : 'N/A'}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
      </div>
    </div>
  );
}
